import { CheckCircleOutlined, CloseCircleOutlined, FilterOutlined } from '@ant-design/icons';
import { Button, message, Modal, Space, Table, Tag, Tooltip } from 'antd';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PostponementApproval } from '../domain/postponementApprovals';
import useRequest from '../hooks/useRequest';

const PostponementApprovalsTab = () => {
    const { t } = useTranslation();
    const request = useRequest();
    const [loading, setLoading] = useState(false);
    const [approvingAll, setApprovingAll] = useState(false);
    const [approvals, setApprovals] = useState<PostponementApproval[]>([]);
    const [showPendingOnly, setShowPendingOnly] = useState(false);

    const fetchApprovals = useCallback(async () => {
        setLoading(true);
        try {
            const data = await request<PostponementApproval[]>({
                path: '/postponement-approvals',
                method: 'GET',
            });
            setApprovals(data);
        } catch (error) {
            console.error('Failed to fetch approvals:', error);
            message.error(t('controlRoom.approvals.fetchError', 'Failed to fetch approvals'));
        } finally {
            setLoading(false);
        }
    }, [request, t]);

    useEffect(() => {
        fetchApprovals();
    }, [fetchApprovals]);

    const handleApprove = async (id: number) => {
        try {
            await request({
                path: `/postponement-approvals/${id}/approve`,
                method: 'POST',
            });
            message.success(t('controlRoom.approvals.approveSuccess', 'Approval granted'));
            fetchApprovals();
        } catch (error) {
            console.error('Failed to approve:', error);
            message.error(t('controlRoom.approvals.approveError', 'Failed to approve'));
        }
    };

    const handleReject = async (id: number) => {
        try {
            await request({
                path: `/postponement-approvals/${id}/reject`,
                method: 'POST',
            });
            message.success(t('controlRoom.approvals.rejectSuccess', 'Approval rejected'));
            fetchApprovals();
        } catch (error) {
            console.error('Failed to reject:', error);
            message.error(t('controlRoom.approvals.rejectError', 'Failed to reject'));
        }
    };

    const handleApproveAll = async () => {
        setApprovingAll(true);
        try {
            const result = await request<{ count: number }>({
                path: '/postponement-approvals/approve-all',
                method: 'POST',
            });
            message.success(
                t('controlRoom.approvals.approveAllSuccess', {
                    defaultValue: '{{count}} approvals granted',
                    count: result.count,
                }),
            );
            await fetchApprovals();
        } catch (error) {
            console.error('Failed to approve all:', error);
            message.error(t('controlRoom.approvals.approveAllError', 'Failed to approve all'));
        } finally {
            setApprovingAll(false);
        }
    };

    const confirmApproveAll = () => {
        Modal.confirm({
            title: t('controlRoom.approvals.approveAllConfirmTitle', 'Approve all pending requests?'),
            content: t(
                'controlRoom.approvals.approveAllConfirmContent',
                'All pending postponement requests will be approved.',
            ),
            okText: t('controlRoom.approvals.approveAllConfirm', 'Approve all'),
            cancelText: t('common.cancel', 'Cancel'),
            okButtonProps: { danger: true },
            onOk: handleApproveAll,
        });
    };

    const hasPendingApprovals = approvals.some(approval => !approval.aprobat && !approval.refuzat);
    const displayedApprovals = showPendingOnly
        ? approvals.filter(approval => !approval.aprobat && !approval.refuzat)
        : approvals;

    const columns = [
        {
            title: t('controlRoom.approvals.id', 'ID'),
            dataIndex: 'id',
            key: 'id',
            width: 80,
        },
        {
            title: t('controlRoom.approvals.contract', 'Contract'),
            key: 'contract',
            render: (_: unknown, record: PostponementApproval) => (
                <Space direction="vertical" size={0}>
                    <strong>{record.contractNumber ?? record.nrContract}</strong>
                    <span style={{ fontSize: '0.85em', color: 'gray' }}>{record.clientName}</span>
                </Space>
            ),
        },
        {
            title: t('controlRoom.approvals.dates', 'Dates'),
            key: 'dates',
            render: (_: unknown, record: PostponementApproval) => (
                <Space direction="vertical" size={0}>
                    <span>
                        {t('controlRoom.approvals.from', 'From')}:{' '}
                        {record.termenInitial ? record.termenInitial.split('T')[0] : '-'}
                    </span>
                    <span>
                        {t('controlRoom.approvals.to', 'To')}:{' '}
                        {record.termenAmanare ? record.termenAmanare.split('T')[0] : '-'}
                    </span>
                </Space>
            ),
        },
        {
            title: t('controlRoom.approvals.days', 'Days'),
            dataIndex: 'zile',
            key: 'zile',
            width: 80,
        },
        {
            title: t('controlRoom.approvals.requester', 'Requested By'),
            key: 'requester',
            render: (_: unknown, record: PostponementApproval) => (
                <Space direction="vertical" size={0}>
                    <span>{record.requesterName ?? '-'}</span>
                    <span style={{ fontSize: '0.85em', color: 'gray' }}>
                        {record.stamp ? new Date(record.stamp).toLocaleString('ro-RO') : '-'}
                    </span>
                </Space>
            ),
        },
        {
            title: t('controlRoom.approvals.status', 'Status'),
            key: 'status',
            render: (_: unknown, record: PostponementApproval) => {
                if (record.aprobat) {
                    return (
                        <Tag color="success">{t('controlRoom.approvals.approved', 'Approved')}</Tag>
                    );
                }
                if (record.refuzat) {
                    return (
                        <Tag color="error">{t('controlRoom.approvals.rejected', 'Rejected')}</Tag>
                    );
                }
                return (
                    <Tag color="processing">{t('controlRoom.approvals.pending', 'Pending')}</Tag>
                );
            },
        },
        {
            title: t('controlRoom.approvals.actions', 'Actions'),
            key: 'actions',
            render: (_: unknown, record: PostponementApproval) => {
                if (!record.aprobat && !record.refuzat) {
                    return (
                        <Space>
                            <Tooltip title={t('controlRoom.approvals.approve', 'Approve')}>
                                <Button
                                    type="primary"
                                    icon={<CheckCircleOutlined />}
                                    onClick={() => handleApprove(record.id)}
                                    size="small"
                                    style={{ backgroundColor: '#52c41a' }}
                                />
                            </Tooltip>
                            <Tooltip title={t('controlRoom.approvals.reject', 'Reject')}>
                                <Button
                                    type="primary"
                                    danger
                                    icon={<CloseCircleOutlined />}
                                    onClick={() => handleReject(record.id)}
                                    size="small"
                                />
                            </Tooltip>
                        </Space>
                    );
                }
                return (
                    <Space direction="vertical" size={0}>
                        <span style={{ fontSize: '0.85em', color: 'gray' }}>
                            {t('controlRoom.approvals.by', 'By')}: {record.approverName ?? '-'}
                        </span>
                        <span style={{ fontSize: '0.85em', color: 'gray' }}>
                            {record.dataAprobarii
                                ? new Date(record.dataAprobarii).toLocaleDateString('ro-RO')
                                : '-'}
                        </span>
                    </Space>
                );
            },
        },
    ];

    return (
        <div style={{ padding: '24px 0' }}>
            <Space style={{ marginBottom: 16 }}>
                <Button
                    icon={<FilterOutlined />}
                    type={showPendingOnly ? 'primary' : 'default'}
                    aria-pressed={showPendingOnly}
                    onClick={() => setShowPendingOnly(previousValue => !previousValue)}
                >
                    {showPendingOnly
                        ? t('controlRoom.approvals.showAll', 'Show all')
                        : t('controlRoom.approvals.pendingOnly', 'Pending only')}
                </Button>
                <Button
                    type="primary"
                    icon={<CheckCircleOutlined />}
                    onClick={confirmApproveAll}
                    disabled={!hasPendingApprovals}
                    loading={approvingAll}
                    style={{ backgroundColor: '#52c41a' }}
                >
                    {t('controlRoom.approvals.approveAll', 'Approve all')}
                </Button>
            </Space>
            <Table
                columns={columns}
                dataSource={displayedApprovals}
                rowKey="id"
                loading={loading}
                pagination={{ pageSize: 20 }}
                size="middle"
            />
        </div>
    );
};

export default PostponementApprovalsTab;
